import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddressSearch } from '@/components/AddressSearch';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Permission } from '@construction/shared';
import { geocodeApi, sitesApi, type Site } from '@/api/endpoints';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  FilterBar,
  Input,
  ListGroup,
  ListRow,
  PageHeader,
  Select,
  Spinner,
  TableRow,
} from '@/components/ui';
import { GeofenceMap } from '@/components/GeofenceMap';
import { getCurrentFix, LocationError } from '@/native';
import { TIME_ZONES, defaultTimeZone, timeZoneLabel } from '@/lib/timezones';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { usePermissions } from '@/lib/permissions';
import { StatusTabs } from '@/features/filters/StatusTabs';
import { OrgFilter, OrgSelect, type OrgChoice } from '@/features/orgs';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { siteLanding } from '@/lib/routes/logic';
import { useAuthStore } from '@/store/auth';

export function SitesPage() {
  const { t } = useTranslation();
  const { can, isPlatform } = usePermissions();
  // 建工地要 site.create（CR-038）。其余人只看到自己参与的工地。
  const isAdmin = can(Permission.SITE_CREATE);
  const [creating, setCreating] = useState(false);
  // 在建 / 已归档分开列（CR-072）。改之前只列在建的，归档过的工地在管理端根本找不到入口
  const [tab, setTab] = useState<'ACTIVE' | 'ARCHIVED'>('ACTIVE');

  // 平台账号按单位筛（CR-073）。工地列表不分页、一次全拿回来，在这里筛就够了；搜索也一样
  const [org, setOrg] = useState('');
  const [search, setSearch] = useState('');

  // 单独一个 key：`['sites']` 是切换器在用的（只列在建的），别把归档的混进去
  const all = useQuery({
    queryKey: ['sites', 'all'],
    queryFn: () => sitesApi.list(true),
  });
  const { isLoading, isError, error, refetch } = all;
  const needle = search.trim().toLowerCase();
  const inOrg = (all.data ?? []).filter(
    (s) =>
      (org === '' || s.orgId === Number(org)) &&
      (needle === '' || `${s.name} ${s.nameEn ?? ''} ${s.address ?? ''}`.toLowerCase().includes(needle)),
  );
  const active = inOrg.filter((s) => s.status === 'ACTIVE');
  const archived = inOrg.filter((s) => s.status !== 'ACTIVE');
  const data = all.data ? (tab === 'ACTIVE' ? active : archived) : undefined;
  const toMessage = useErrorMessage();

  return (
    <>
      <PageHeader
        title={t('site.title')}
        action={
          isAdmin ? (
            <Button onClick={() => setCreating(true)} data-action="site.create">
              {t('site.create')}
            </Button>
          ) : undefined
        }
      />

      <Drawer open={creating} size="wide" title={t('site.create')} onClose={() => setCreating(false)}>
        <CreateSiteForm onDone={() => setCreating(false)} />
      </Drawer>

      {all.data && (
        <StatusTabs
          tabs={[
            { value: 'ACTIVE', label: t('site.statusActive'), count: active.length },
            { value: 'ARCHIVED', label: t('site.statusArchived'), count: archived.length },
          ]}
          value={tab}
          onChange={(next) => setTab(next ?? 'ACTIVE')}
        />
      )}

      {/* 工地多了才用得上：搜名称和地址，平台账号多一个公司 */}
      {all.data && all.data.length > 0 && (
        <FilterBar>
          <div className="min-w-56 max-w-xs flex-1 max-md:max-w-none max-md:basis-full">
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('site.searchSitesAddress')}
              aria-label={t('app.search')}
            />
          </div>
          {isPlatform && <OrgFilter value={org} onChange={setOrg} />}
        </FilterBar>
      )}

      {isLoading && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}

      {isError && (
        <div className="flex flex-col items-start gap-3">
          <Alert>{toMessage(error)}</Alert>
          <Button variant="secondary" onClick={() => void refetch()}>
            {t('app.retry')}
          </Button>
        </div>
      )}

      {data && data.length === 0 && (needle !== '' || org !== '') && <EmptyState title={t('site.noMatches')} />}
      {data && data.length === 0 && needle === '' && org === '' && tab === 'ARCHIVED' && (
        <EmptyState title={t('site.noArchived')} />
      )}
      {data && data.length === 0 && needle === '' && org === '' && tab === 'ACTIVE' && (
        <EmptyState
          title={t('site.empty')}
          hint={isAdmin ? t('site.emptyHintAdmin') : t('site.emptyHintWorker')}
        />
      )}

      {data && data.length > 0 && (
        <>
          <ListGroup className="xl:hidden">
            {data.map((site) => (
              <SiteListRow key={site.id} site={site} />
            ))}
          </ListGroup>
          <DataTable
            minWidth="52rem"
            columns={[
              { key: 'name', label: t('site.title') },
              // 归哪家只对平台账号有意义（CR-073）
              ...(isPlatform ? [{ key: 'org', label: t('org.column'), className: 'w-36' }] : []),
              { key: 'members', label: t('site.membersColumn'), align: 'right' as const, className: 'w-24' },
              { key: 'fence', label: t('site.geofence'), align: 'right' as const, className: 'w-28' },
              { key: 'tz', label: t('site.timezone'), className: 'w-36' },
              { key: 'status', label: t('site.status'), className: 'w-28' },
            ]}
          >
            {data.map((site) => (
              <SiteTableRow key={site.id} site={site} />
            ))}
          </DataTable>
        </>
      )}
    </>
  );
}

/** 时区只写城市那一段：`America/Los_Angeles` → Los Angeles。完整的在工地设置里 */
const zoneCity = (tz: string) => (tz.split('/').pop() ?? tz).replace(/_/g, ' ');

/**
 * 点一个工地去哪由路由表决定（CR-040）：
 * - 网页端：进这个工地里第一个能看的页面。改之前一律去概览 —— 只有「管理账号」
 *   「工地设置」这类权限的人点进去撞上概览的门槛，被弹回工地列表，看着像没反应；
 *   工地里一页都看不了的人，这一行点不开。
 *   **已归档的送去工地设置**（CR-079，客户原话「归档的工地跳去工地设置里。因为归档后的概览是空白」）——
 *   判断在 `siteLanding` 里，别在这里写死路径。
 * - App 里（我的 → 管理 → 工地）：把它设成当前工地、回首页。
 *   改之前链的是网页端地址，在手机上一点就绕一圈回到首页。
 *   在班锁定期间（约束 9）点不开 —— 改之前直接写 localStorage，绕过了锁定（CR-042）。
 *   **已归档的在 App 里也点不开**（CR-079）：切换器里只有在建的工地，把归档的设成当前工地
 *   会被静默换到别的工地去（`useMobileSite` 找不到它就落回默认），点了像没反应、实际还换了工地。
 */
function useOpenSite(site: Site): (() => void) | null {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const inMobileShell = useLocation().pathname.startsWith('/m');
  const navigate = useNavigate();
  const { setSite, locked } = useMobileSite();
  const archived = site.status !== 'ACTIVE';

  if (inMobileShell) {
    if (locked || archived) return null;
    return () => {
      setSite(site.id);
      navigate('/m');
    };
  }
  const target = siteLanding(role, site.id, { archived });
  return target ? () => navigate(target) : null;
}

function StatusBadge({ site }: { site: Site }) {
  const { t } = useTranslation();
  return (
    <Badge tone={site.status === 'ACTIVE' ? 'ok' : 'neutral'}>
      {site.status === 'ACTIVE' ? t('site.statusActive') : t('site.statusArchived')}
    </Badge>
  );
}

function SiteTableRow({ site }: { site: Site }) {
  const { t } = useTranslation();
  const { isPlatform } = usePermissions();
  const open = useOpenSite(site);
  const cells = (
    <>
      <td>
        <span className="block truncate font-medium text-ink">{site.name}</span>
        {site.address && <span className="block truncate text-caption text-ink-3">{site.address}</span>}
      </td>
      {isPlatform && <td className="truncate text-ink-2">{site.org.name}</td>}
      <td className="whitespace-nowrap text-right tabular-nums text-ink-2">
        {site.memberCount !== undefined ? t('site.memberCount', { count: site.memberCount }) : '—'}
      </td>
      <td className="whitespace-nowrap text-right tabular-nums text-ink-2">{site.geofenceRadiusM} m</td>
      <td className="truncate text-ink-2">{zoneCity(site.timezone)}</td>
      <td className="whitespace-nowrap">
        <StatusBadge site={site} />
      </td>
    </>
  );
  return open ? <TableRow onOpen={open}>{cells}</TableRow> : <tr>{cells}</tr>;
}

function SiteListRow({ site }: { site: Site }) {
  const { t } = useTranslation();
  const { isPlatform } = usePermissions();
  const open = useOpenSite(site);
  const inMobileShell = useLocation().pathname.startsWith('/m');
  const archived = site.status !== 'ACTIVE';
  return (
    <ListRow
      onClick={open ?? undefined}
      title={site.name}
      subtitle={[isPlatform ? site.org.name : null, site.address].filter(Boolean).join(' · ') || undefined}
      // App 里归档的工地点不开，写明为什么（CR-079）
      excerpt={inMobileShell && archived ? t('site.archivedMobileHint') : undefined}
      trailing={
        <>
          {site.memberCount !== undefined && (
            <span className="tabular-nums">{t('site.memberCount', { count: site.memberCount })}</span>
          )}
          {archived && <StatusBadge site={site} />}
        </>
      }
    />
  );
}

function CreateSiteForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const { isPlatform } = usePermissions();
  // 归哪家（CR-073）：平台账号必须选，没有「平台」这一项 —— 平台自己没有工地。单位账号建的永远是自己家的
  const [orgChoice, setOrgChoice] = useState<OrgChoice>('');

  const [form, setForm] = useState({
    name: '',
    address: '',
    lat: '',
    lng: '',
    // 150 只是个起点，建完之后随时能在工地设置里改
    geofenceRadiusM: '150',
    /**
     * **时区必须在建工地时就选**（CR-027）。
     *
     * 改之前这个表单根本没有这个字段，接口收了却没人传，于是每个新工地
     * 都落成 schema 的默认值 `America/Los_Angeles`。客户人在卡尔加里，
     * 他建的每个工地默认都是错的 —— 而时区错了，「今天」的分界线就错，
     * 考勤按天分组、日报的日期全跟着偏，**还不报错**。
     *
     * 默认取建工地这个人浏览器所在的时区：他多半就在工地所在的地区。
     */
    timezone: defaultTimeZone(),
  });
  const [error, setError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [picked, setPicked] = useState(false);

  const lat = Number(form.lat);
  const lng = Number(form.lng);
  const radius = Number(form.geofenceRadiusM);
  const previewReady =
    form.lat !== '' && form.lng !== '' && Number.isFinite(lat) && Number.isFinite(lng) && radius >= 50;

  /** 管理员常常就站在工地上建站，直接取当前位置最准 */
  async function useCurrentLocation() {
    setError(null);
    setLocating(true);
    try {
      const fix = await getCurrentFix();
      setForm((f) => ({ ...f, lat: fix.lat.toFixed(6), lng: fix.lng.toFixed(6) }));

      // 顺手把地址反查回来填上。查不到就算了 —— 坐标已经拿到了，
      // 地址只是给人看的，不该因为它失败就让整个操作显示成失败。
      try {
        const place = await geocodeApi.reverse(fix.lat, fix.lng);
        if (place) setForm((f) => (f.address.trim() ? f : { ...f, address: place.address }));
      } catch {
        /* 忽略 */
      }
    } catch (err) {
      setError(
        err instanceof LocationError && err.kind === 'DENIED'
          ? t('errors.PUNCH_LOCATION_REQUIRED')
          : t('site.locateFailed'),
      );
    } finally {
      setLocating(false);
    }
  }

  const mutation = useMutation({
    mutationFn: () =>
      sitesApi.create({
        name: form.name.trim(),
        ...(isPlatform ? { orgId: orgChoice as number } : {}),
        address: form.address.trim() || undefined,
        lat: Number(form.lat),
        lng: Number(form.lng),
        geofenceRadiusM: Number(form.geofenceRadiusM),
        timezone: form.timezone,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      onDone();
    },
    onError: (err) => setError(toMessage(err)),
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (isPlatform && orgChoice === '') {
      setError(t('errors.ORG_REQUIRED'));
      return;
    }
    mutation.mutate();
  }

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error && <Alert>{error}</Alert>}

      {isPlatform && (
        <OrgSelect
          id="site-org"
          label={t('site.org')}
          value={orgChoice}
          onChange={setOrgChoice}
          hint={t('site.orgHint')}
        />
      )}

      <Field label={t('site.name')} htmlFor="site-name" required>
        <Input id="site-name" value={form.name} onChange={set('name')} required maxLength={120} />
      </Field>

      <Field label={t('site.address')} htmlFor="site-address" hint={picked ? t('site.addressPicked') : undefined}>
        <AddressSearch
          id="site-address"
          value={form.address}
          onChange={(address) => setForm((f) => ({ ...f, address }))}
          onPick={(result) => {
            // 只填地址和坐标，不碰工地名。
            // Nominatim 给的短名往往是那个地址上的商户（搜「600 W 7th St」
            // 第一条是家餐厅），拿它当工地名会得到「Sugarfish」这种东西，
            // 管理员没留意就存进去了。
            setForm((f) => ({
              ...f,
              address: result.address,
              lat: result.lat.toFixed(6),
              lng: result.lng.toFixed(6),
            }));
            setPicked(true);
          }}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('site.latitude')} htmlFor="site-lat" required>
          <Input
            id="site-lat"
            value={form.lat}
            onChange={set('lat')}
            inputMode="decimal"
            placeholder="34.0476"
            required
          />
        </Field>
        <Field label={t('site.longitude')} htmlFor="site-lng" required>
          <Input
            id="site-lng"
            value={form.lng}
            onChange={set('lng')}
            inputMode="decimal"
            placeholder="-118.2578"
            required
          />
        </Field>
      </div>

      <div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void useCurrentLocation()}
          loading={locating}
        >
          {t('site.useCurrentLocation')}
        </Button>
      </div>

      <Field label={t('site.timezone')} htmlFor="site-tz" hint={t('site.timezoneCreateHint')}>
        <Select id="site-tz" value={form.timezone} onChange={set('timezone')}>
          {TIME_ZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {timeZoneLabel(tz)}
            </option>
          ))}
        </Select>
      </Field>

      <Field label={t('site.geofenceRadius')} htmlFor="site-radius" hint={t('site.geofenceHint')}>
        <div className="flex items-center gap-2">
          <Input
            id="site-radius"
            value={form.geofenceRadiusM}
            onChange={set('geofenceRadiusM')}
            inputMode="numeric"
            min={50}
            max={5000}
            step={10}
            type="number"
            className="max-w-32"
          />
          <span className="text-body text-ink-3">{t('site.meters')}</span>
        </div>
      </Field>

      {previewReady && (
        <div>
          <p className="mb-2 text-caption text-ink-3">{t('site.mapHint')}</p>
          <GeofenceMap
            lat={lat}
            lng={lng}
            radiusM={radius}
            heightClass="h-56"
            onMove={(nextLat, nextLng) =>
              setForm((f) => ({ ...f, lat: nextLat.toFixed(6), lng: nextLng.toFixed(6) }))
            }
          />
        </div>
      )}

      <div className="flex gap-3">
        <Button type="submit" loading={mutation.isPending}>
          {t('site.create')}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t('app.cancel')}
        </Button>
      </div>
    </form>
  );
}
